// SalesCards.js
import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Paper,
  Divider,
  Grid,
  LinearProgress,
  useTheme,
  alpha,
  Chip,
  IconButton,
  Tooltip,
  Zoom,
  Fade,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase-config";
import dayjs from "dayjs";
import {
  TrendingUp,
  Calendar,
  DollarSign,
  CheckCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const SalesCards = () => {
  const theme = useTheme();
  const [selectedMonth, setSelectedMonth] = useState(dayjs().format("YYYY-MM"));
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [monthlyStats, setMonthlyStats] = useState({ total: 0, paid: 0, unpaid: 0 });
  const [dailyStats, setDailyStats] = useState({ total: 0, paid: 0, unpaid: 0 });
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthlyTrend, setMonthlyTrend] = useState(0);
  const [dailyTrend, setDailyTrend] = useState(0);
  const [monthlyData, setMonthlyData] = useState([]); // Added this state
  const [dailyData, setDailyData] = useState([]); // Added this state

  useEffect(() => {
    const fetchInvoices = async () => {
      setLoading(true);
      try {
        const invoicesRef = collection(db, "invoices");
        const snapshot = await getDocs(invoicesRef);
        const fetchedInvoices = snapshot.docs.map((doc) => doc.data());
        setInvoices(fetchedInvoices);
      } catch (error) {
        console.error("Error fetching invoices:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, []);

  useEffect(() => {
    const monthData = invoices.filter((invoice) =>
      dayjs(invoice.createdAt).format("YYYY-MM") === selectedMonth
    );
    
    setMonthlyData(monthData); // Store the filtered data

    const total = monthData.reduce((sum, invoice) => {
      const invoiceTotal = invoice.items.reduce(
        (itemSum, item) => itemSum + (item.price * item.quantity || 0),
        0
      );
      return sum + invoiceTotal;
    }, 0);

    const paid = monthData
      .filter((invoice) => invoice.isBillPaid)
      .reduce((sum, invoice) => {
        const invoiceTotal = invoice.items.reduce(
          (itemSum, item) => itemSum + (item.price * item.quantity || 0),
          0
        );
        return sum + invoiceTotal;
      }, 0);

    const unpaid = total - paid;
    setMonthlyStats({ total, paid, unpaid });

    // Calculate trend
    const prevMonth = dayjs(selectedMonth).subtract(1, 'month').format("YYYY-MM");
    const prevMonthData = invoices.filter((invoice) =>
      dayjs(invoice.createdAt).format("YYYY-MM") === prevMonth
    );
    const prevTotal = prevMonthData.reduce((sum, invoice) => {
      const invoiceTotal = invoice.items.reduce(
        (itemSum, item) => itemSum + (item.price * item.quantity || 0),
        0
      );
      return sum + invoiceTotal;
    }, 0);
    setMonthlyTrend(prevTotal ? ((total - prevTotal) / prevTotal) * 100 : 0);
  }, [selectedMonth, invoices]);

  useEffect(() => {
    const dayString = selectedDate.format("YYYY-MM-DD");
    const dayData = invoices.filter(
      (invoice) => dayjs(invoice.createdAt).format("YYYY-MM-DD") === dayString
    );

    setDailyData(dayData); // Store the filtered data

    const total = dayData.reduce((sum, invoice) => {
      const invoiceTotal = invoice.items.reduce(
        (itemSum, item) => itemSum + (item.price * item.quantity || 0),
        0
      );
      return sum + invoiceTotal;
    }, 0);

    const paid = dayData
      .filter((invoice) => invoice.isBillPaid)
      .reduce((sum, invoice) => {
        const invoiceTotal = invoice.items.reduce(
          (itemSum, item) => itemSum + (item.price * item.quantity || 0),
          0
        );
        return sum + invoiceTotal;
      }, 0);

    const unpaid = total - paid;
    setDailyStats({ total, paid, unpaid });

    // Calculate trend
    const prevDay = selectedDate.subtract(1, 'day').format("YYYY-MM-DD");
    const prevDayData = invoices.filter(
      (invoice) => dayjs(invoice.createdAt).format("YYYY-MM-DD") === prevDay
    );
    const prevTotal = prevDayData.reduce((sum, invoice) => {
      const invoiceTotal = invoice.items.reduce(
        (itemSum, item) => itemSum + (item.price * item.quantity || 0),
        0
      );
      return sum + invoiceTotal;
    }, 0);
    setDailyTrend(prevTotal ? ((total - prevTotal) / prevTotal) * 100 : 0);
  }, [selectedDate, invoices]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getPaymentPercentage = (paid, total) => {
    if (total === 0) return 0;
    return (paid / total) * 100;
  };

  const generateMonthOptions = () => {
    const options = [];
    const startDate = dayjs("2024-12-01");
    const currentDate = dayjs();
    
    let month = startDate;
    while (month.isBefore(currentDate) || month.isSame(currentDate, 'month')) {
      const monthValue = month.format("YYYY-MM");
      options.push(
        <MenuItem key={monthValue} value={monthValue}>
          {month.format("MMMM YYYY")}
        </MenuItem>
      );
      month = month.add(1, 'month');
    }
    return options;
  };

  const TrendIndicator = ({ value }) => {
    const isPositive = value >= 0;
    return (
      <Box display="flex" alignItems="center" gap={0.5}>
        {isPositive ? (
          <TrendingUp size={14} color={theme.palette.success.main} />
        ) : (
          <TrendingUp size={14} color={theme.palette.error.main} style={{ transform: 'rotate(180deg)' }} />
        )}
        <Typography
          variant="caption"
          sx={{
            color: isPositive ? theme.palette.success.main : theme.palette.error.main,
            fontWeight: 500,
          }}
        >
          {Math.abs(value).toFixed(1)}%
        </Typography>
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Fade in={true} timeout={500}>
        <Box>
          {loading && (
            <LinearProgress 
              sx={{ 
                mb: 2, 
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                '& .MuiLinearProgress-bar': {
                  backgroundColor: theme.palette.primary.main,
                }
              }} 
            />
          )}
          
          <Grid container spacing={3}>
            {/* Monthly Sales Card */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  overflow: 'hidden',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[4],
                  },
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.light, 0.1)} 100%)`,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Calendar size={20} color={theme.palette.primary.main} />
                      <Typography variant="h6" fontWeight="bold" sx={{ color: theme.palette.text.primary }}>
                        Monthly Sales
                      </Typography>
                    </Box>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <Select
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        displayEmpty
                        sx={{ 
                          borderRadius: 2,
                          backgroundColor: theme.palette.background.paper,
                        }}
                      >
                        {generateMonthOptions()}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>

                <CardContent sx={{ p: 3 }}>
                  <Box mb={3}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Total Revenue
                    </Typography>
                    <Box display="flex" alignItems="baseline" justifyContent="space-between">
                      <Typography variant="h4" fontWeight="bold" sx={{ color: theme.palette.text.primary }}>
                        {formatCurrency(monthlyStats.total)}
                      </Typography>
                      <TrendIndicator value={monthlyTrend} />
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CheckCircle size={16} color={theme.palette.success.main} />
                        <Typography variant="body2">Paid</Typography>
                      </Box>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(monthlyStats.paid)}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={getPaymentPercentage(monthlyStats.paid, monthlyStats.total)}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: alpha(theme.palette.success.main, 0.1),
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: theme.palette.success.main,
                          borderRadius: 4,
                        },
                        mb: 2,
                      }}
                    />

                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <AlertCircle size={16} color={theme.palette.warning.main} />
                        <Typography variant="body2">Unpaid</Typography>
                      </Box>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(monthlyStats.unpaid)}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={getPaymentPercentage(monthlyStats.unpaid, monthlyStats.total)}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: alpha(theme.palette.warning.main, 0.1),
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: theme.palette.warning.main,
                          borderRadius: 4,
                        },
                      }}
                    />
                  </Box>

                  <Box 
                    sx={{ 
                      mt: 3, 
                      pt: 2, 
                      borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Chip
                      label={`${monthlyData.length} invoice${monthlyData.length !== 1 ? 's' : ''}`}
                      size="small"
                      sx={{ borderRadius: 2 }}
                    />
                    <Chip
                      label={`${((monthlyStats.paid / monthlyStats.total) * 100 || 0).toFixed(1)}% paid`}
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ borderRadius: 2 }}
                    />
                  </Box>
                </CardContent>
              </Paper>
            </Grid>

            {/* Daily Sales Card */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  overflow: 'hidden',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[4],
                  },
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.light, 0.1)} 100%)`,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" gap={1}>
                      <TrendingUp size={20} color={theme.palette.secondary.main} />
                      <Typography variant="h6" fontWeight="bold" sx={{ color: theme.palette.text.primary }}>
                        Daily Sales
                      </Typography>
                    </Box>
                    <DatePicker
                      value={selectedDate}
                      onChange={(date) => setSelectedDate(dayjs(date))}
                      slotProps={{ 
                        textField: { 
                          size: 'small',
                          sx: { 
                            minWidth: 150,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              backgroundColor: theme.palette.background.paper,
                            }
                          }
                        } 
                      }}
                    />
                  </Box>
                </Box>

                <CardContent sx={{ p: 3 }}>
                  <Box mb={3}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Total Revenue
                    </Typography>
                    <Box display="flex" alignItems="baseline" justifyContent="space-between">
                      <Typography variant="h4" fontWeight="bold" sx={{ color: theme.palette.text.primary }}>
                        {formatCurrency(dailyStats.total)}
                      </Typography>
                      <TrendIndicator value={dailyTrend} />
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CheckCircle size={16} color={theme.palette.success.main} />
                        <Typography variant="body2">Paid</Typography>
                      </Box>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(dailyStats.paid)}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={getPaymentPercentage(dailyStats.paid, dailyStats.total)}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: alpha(theme.palette.success.main, 0.1),
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: theme.palette.success.main,
                          borderRadius: 4,
                        },
                        mb: 2,
                      }}
                    />

                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <AlertCircle size={16} color={theme.palette.warning.main} />
                        <Typography variant="body2">Unpaid</Typography>
                      </Box>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(dailyStats.unpaid)}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={getPaymentPercentage(dailyStats.unpaid, dailyStats.total)}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: alpha(theme.palette.warning.main, 0.1),
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: theme.palette.warning.main,
                          borderRadius: 4,
                        },
                      }}
                    />
                  </Box>

                  <Box 
                    sx={{ 
                      mt: 3, 
                      pt: 2, 
                      borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Chip
                      label={dayjs(selectedDate).format("dddd, MMMM D, YYYY")}
                      size="small"
                      sx={{ borderRadius: 2 }}
                    />
                    <Chip
                      label={`${dailyData.length} order${dailyData.length !== 1 ? 's' : ''}`}
                      size="small"
                      color="info"
                      variant="outlined"
                      sx={{ borderRadius: 2 }}
                    />
                  </Box>
                </CardContent>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Fade>
    </LocalizationProvider>
  );
};

export default SalesCards;